import type { ColDef, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { colorSchemeDark, colorSchemeLight, themeQuartz } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import styled from 'styled-components';
import { useElementSize } from 'usehooks-ts';
import { useAgGridTheme, useTheme, type ThemeConfig } from '../../../theme';
import type { TableBlock as TableBlockType } from '../../../types/blocks';
import { validateTable } from '../../../validators/tables';

interface TableBlockProps {
  block: TableBlockType;
}

const TableBlock = ({ block }: TableBlockProps) => {
  const theme = useTheme();
  const agGridTheme = useAgGridTheme(
    theme.mode === 'dark'
      ? themeQuartz.withPart(colorSchemeDark)
      : themeQuartz.withPart(colorSchemeLight)
  );

  // Validate table data before rendering
  const validatedTable = validateTable(block);
  const { title, columns, data, config } = validatedTable;
  const [quickFilterText, setQuickFilterText] = useState('');
  const [tableContainerRef, { width }] = useElementSize();
  const gridApiRef = useRef<any>(null);

  // Number formatter for large numbers
  const numberFormatter = useCallback((params: any) => {
    if (typeof params.value === 'number' && params.value > 1000) {
      return params.value.toLocaleString();
    }
    return params.value;
  }, []);

  // Create AG-Grid column definitions
  const columnDefs: ColDef[] = useMemo(() => {
    return columns.map((col) => ({
      field: col.key,
      headerName: col.header,
      headerTooltip: (col as any).description || col.header, // Add tooltip for headers
      sortable: config?.enableSorting && col.sortable !== false,
      filter: config?.enableFiltering && col.filterable !== false,
      width: col.width,
      cellStyle: { textAlign: col.align || 'left' },
      headerClass: col.align ? `ag-header-cell-${col.align}` : undefined,
      valueFormatter: typeof data[0]?.[col.key] === 'number' ? numberFormatter : undefined
    }));
  }, [columns, config, data, numberFormatter]);

  // AG-Grid options
  const gridOptions: GridOptions = useMemo(
    () => ({
      columnDefs,
      rowData: data,
      defaultColDef: {
        resizable: true,
        sortable: config?.enableSorting ?? true,
        filter: config?.enableFiltering ?? false,
        floatingFilter: false, // Disable floating filters to show icon only in header
        flex: 1, // Make columns flexible
        minWidth: 100 // Minimum width for readability
      },
      // Auto-enable pagination for large datasets
      pagination: config?.enablePagination ?? data.length > 20,
      paginationPageSize: config?.pageSize || 20,
      domLayout: 'autoHeight',
      suppressPaginationPanel: false,
      animateRows: true,
      // Enable cell selection
      rowSelection: 'multiple',
      cellSelection: true,
      suppressCellFocus: false,
      suppressRowHoverHighlight: false,
      // Enable header tooltips
      tooltipShowDelay: 0,
      tooltipHideDelay: 2000,
      // Disable export options in context menu
      suppressContextMenu: false,
      getContextMenuItems: (params: any) => {
        const result = params.defaultItems || [];
        // Filter out export-related menu items
        return result.filter((item: string) => {
          return !['csvExport', 'excelExport', 'export'].includes(item);
        });
      }
    }),
    [columnDefs, data, config]
  );

  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridApiRef.current = params.api;
    // Auto-size columns to fit container width
    params.api.sizeColumnsToFit();
  }, []);

  // Handle responsive resize
  useEffect(() => {
    const handleResize = () => {
      if (gridApiRef.current) {
        gridApiRef.current.sizeColumnsToFit();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Resize columns when container width changes
  useEffect(() => {
    if (gridApiRef.current && width > 0) {
      gridApiRef.current.sizeColumnsToFit();
    }
  }, [width]);

  const onQuickFilterChanged = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setQuickFilterText(value);
  }, []);

  return (
    <TableContainer theme={theme}>
      {title && <TableTitle theme={theme}>{title}</TableTitle>}

      {config?.enableFiltering && (
        <FilterContainer>
          <SearchInputContainer>
            <SearchIcon theme={theme}>
              <FiSearch />
            </SearchIcon>
            <GlobalFilter
              theme={theme}
              value={quickFilterText}
              onChange={onQuickFilterChanged}
              placeholder="Search all columns..."
            />
          </SearchInputContainer>
        </FilterContainer>
      )}

      <GridContainer ref={tableContainerRef}>
        <AgGridReact
          theme={agGridTheme}
          containerStyle={{
            width: '100%'
          }}
          gridOptions={gridOptions}
          onGridReady={onGridReady}
          quickFilterText={quickFilterText}
        />
      </GridContainer>
    </TableContainer>
  );
};

export default TableBlock;

const TableContainer = styled.div<{ theme: ThemeConfig }>`
  margin-top: 12px;
  padding: 8px;
  border-radius: 8px;
  background: ${(props) => (props.theme.mode === 'dark' ? '#1D1D1D' : '#fff')};
  box-shadow: ${(props) =>
    props.theme.mode === 'dark' ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)'};
  display: flex;
  flex-direction: column;
`;

const TableTitle = styled.h3<{ theme: ThemeConfig }>`
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => (props.theme.mode === 'dark' ? '#EEEEEE' : '#333')};
  flex-shrink: 0;
`;

const FilterContainer = styled.div`
  margin-bottom: 8px;
  flex-shrink: 0;
`;

const SearchInputContainer = styled.div`
  position: relative;
  max-width: 300px;
`;

const SearchIcon = styled.div<{ theme: ThemeConfig }>`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${(props) => (props.theme.mode === 'dark' ? '#8B8A8A' : '#666')};
  font-size: 16px;
  pointer-events: none;
`;

const GlobalFilter = styled.input<{ theme: ThemeConfig }>`
  width: 100%;
  padding: 8px 12px 8px 40px;
  border: 1px solid ${(props) => (props.theme.mode === 'dark' ? '#595959' : '#e1e8ed')};
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  background: ${(props) => (props.theme.mode === 'dark' ? '#383838' : '#fff')};
  color: ${(props) => (props.theme.mode === 'dark' ? '#EEEEEE' : '#000')};

  &:focus {
    border-color: #5470c6;
    box-shadow: 0 0 0 3px rgba(84, 112, 198, 0.1);
  }

  &::placeholder {
    color: ${(props) => (props.theme.mode === 'dark' ? '#747474' : '#999')};
  }
`;

const GridContainer = styled.div`
  width: 100%;

  /* Ensure the grid sizes automatically */
  .ag-theme-alpine {
    width: 100%;
  }
`;
